import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import InputConsensus from 'components/InputConsensus';
import CreateIcon from '@mui/icons-material/Create';
import AddIcon from '@mui/icons-material/Add';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import {
  IconButton,
  Tooltip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { makeStyles } from 'utils/makeStylesAdapter';
import { lightTheme } from 'utils/colorsSteps';
import { useParameterKeys } from 'hooks/useParameterKeys';

const useStyles = makeStyles(() => ({
  containerChip: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
}));

const AlternativePhones = props => {
  const {
    phones,
    showInputPhones,
    editInvestigatedInArray,
    handleChangeShowInputPhonesToFalse,
    handleChangeShowInputPhonesToTrue,
    handleRemovePhone,
    handleAddPhone,
    handleChange,
    formState,
    onlyPhonesLabel = false,
  } = props;

  const classes = useStyles();

  const { applyLightTheme } = useParameterKeys();

  const [phoneMask, setPhoneMask] = useState('(99)99999-9999');

  const [displayValue, setDisplayValue] = useState('');

  const formatPhoneDisplay = phone => {
    if (!onlyPhonesLabel || !phone) return phone;

    const digits = String(phone).replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 2)})${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length === 11) {
      return `(${digits.slice(0, 2)})${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
    }

    return phone;
  };

  useEffect(() => {
    if (onlyPhonesLabel && formState.values.phones) {
      setDisplayValue(formatPhoneDisplay(formState.values.phones));
    } else {
      setDisplayValue(formState.values.phones || '');
    }
  }, [formState.values.phones, onlyPhonesLabel]);

  const adjustLabel = str => {
    if (!onlyPhonesLabel) return str;
    return str
      .replace(/Telefones Alternativos/gi, 'Telefones')
      .replace(/Telefone Alternativo/gi, 'Telefone');
  };

  const handleAddPhoneCustom = () => {
    const phoneValue = formState.values.phones;
    if (!phoneValue || phoneValue === '') {
      return;
    }

    handleAddPhone();
  };

  const handlePhoneChange = event => {
    const inputValue = event.target.value;

    if (onlyPhonesLabel) {
      const digits = inputValue.replace(/\D/g, '');
      const newMask = digits.length === 10 ? '(99)9999-9999' : '(99)99999-9999';
      setPhoneMask(newMask);

      setDisplayValue(inputValue);

      event.target.value = digits;
      handleChange(event);
    } else {
      setDisplayValue(inputValue);
      handleChange(event);
    }
  };

  const verifyPhoneMask = event => {
    if (onlyPhonesLabel) {
      const inputValue = event.target.value;
      if (inputValue.match(/\d/g)?.length === 10) {
        setPhoneMask('(99)99999-9999');
      }
    }
  };

  const verifyPhoneOnPaste = event => {
    if (onlyPhonesLabel) {
      setPhoneMask(event.target.value?.length === 10 ? '(99)9999-9999' : '(99)99999-9999');
    }
  };

  return (
    <Box>
      {showInputPhones ? (
        <>
          <Box margin={1} display="flex" justifyContent="flex-end" alignItems="center">
            <InputMask
              onChange={handlePhoneChange}
              value={displayValue}
              mask={phoneMask}
              maskChar={null}
              onBeforeInput={verifyPhoneMask}
              onPaste={verifyPhoneOnPaste}
            >
              {inputProps => (
                <InputConsensus
                  {...inputProps}
                  label={adjustLabel('Telefone Alternativo')}
                  name="phones"
                  type="phones"
                  variant="standard"
                  autoComplete="no"
                  fullWidth
                />
              )}
            </InputMask>
            <IconButton
              color="primary"
              aria-label="add phone"
              onClick={() => handleAddPhoneCustom()}
            >
              <AddIcon style={{ cursor: 'pointer' }} />
            </IconButton>
          </Box>
          <TableContainer component={Paper}>
            <Table className={classes.table} size="small" aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{adjustLabel('Telefones Alternativos')}</TableCell>
                  <TableCell align="center" width={150}>
                    <Box display="flex" position="relative" justifyContent="center">
                      Ações
                      {editInvestigatedInArray && (
                        <Box position="absolute" right={0} top={-3}>
                          <IconButton size="small" onClick={handleChangeShowInputPhonesToFalse}>
                            <IndeterminateCheckBoxIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {phones.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell component="th" scope="row" align="center">
                      {formatPhoneDisplay(item)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleRemovePhone(item)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography align="left">{adjustLabel('Telefones Alternativos')}</Typography>
            <Tooltip title={adjustLabel('Alterar Telefones Alternativos')} arrow placement="right">
              <IconButton color="primary" size="small" onClick={handleChangeShowInputPhonesToTrue}>
                <CreateIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {editInvestigatedInArray &&
            !showInputPhones &&
            (phones.length > 0 ? (
              <Box className={classes.containerChip}>
                {phones.map((phone, i) => (
                  <Chip
                    label={formatPhoneDisplay(phone)}
                    key={i}
                    color="primary"
                    size="small"
                    variant="outlined"
                    style={applyLightTheme ? { color: lightTheme.fontColor, backgroundColor: lightTheme.backgroundColor } : undefined}
                  />
                ))}
              </Box>
            ) : (
              <Typography align="left" variant="h6" className={classes.form}>
                {adjustLabel('Sem telefones alternativos cadastrados')}
              </Typography>
            ))}
        </>
      )}
    </Box>
  );
};

export default AlternativePhones;
